import React from "react";
import { Link } from "react-router-dom";
import "./Services.css";

// ── SVG Icons ──────────────────────────────────────────────
const IconSoil = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 20h18" />
    <path d="M5 20V10l7-7 7 7v10" />
    <path d="M9 20v-5h6v5" />
  </svg>
);

const IconWell = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
  </svg>
);

const IconPump = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="10" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    <line x1="12" y1="15" x2="12" y2="17" />
  </svg>
);

const IconGrout = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2L2 7l10 5 10-5-10-5z" />
    <path d="M2 17l10 5 10-5" />
    <path d="M2 12l10 5 10-5" />
  </svg>
);

const IconFissure = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22V12M12 12L8 7M12 12l4-5" />
    <path d="M5 19l3-4M19 19l-3-4" />
    <circle cx="12" cy="5" r="2" />
  </svg>
);

const IconJet = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const IconMonitor = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const IconShaft = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
    <path d="M7 8h.01M12 8h.01M17 8h.01" />
  </svg>
);

const IconArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// ── Services Data ────────────────────────────────────────────
// Each service now has a unique "slug" — this is used to build
// the URL that the "Learn more" link points to: /services/<slug>
const servicesData = [
  {
    id: "01",
    slug: "soil-investigation",
    icon: <IconSoil />,
    name: "Soil Investigation",
    description:
      "Comprehensive borehole drilling, sampling, and in-situ testing to assess subsurface conditions for safe foundation design.",
    featured: false,
  },
  {
    id: "02",
    slug: "recharge-well",
    icon: <IconWell />,
    name: "Recharge & Relief Well",
    description:
      "Design and installation of recharge and relief wells to manage groundwater levels during deep excavation works.",
    featured: true,
  },
  {
    id: "03",
    slug: "pumping-test",
    icon: <IconPump />,
    name: "Pumping Test",
    description:
      "Field pumping tests to determine aquifer parameters such as hydraulic conductivity and transmissivity.",
    featured: false,
  },
  {
    id: "04",
    slug: "tam-grouting",
    icon: <IconGrout />,
    name: "TAM Grouting",
    description:
      "Tube-à-manchette grouting for ground improvement and permeation grouting around tunnels and deep excavations.",
    featured: false,
  },
  {
    id: "05",
    slug: "fissure-grouting",
    icon: <IconFissure />,
    name: "Fissure Grouting",
    description:
      "Specialist fissure grouting to seal cracks and voids in rock or concrete, preventing water ingress and settlement.",
    featured: false,
  },
  {
    id: "06",
    slug: "jet-grouting",
    icon: <IconJet />,
    name: "JET Grouting",
    description:
      "High-pressure jet grouting to create soil-cement columns for ground stabilization and underpinning.",
    featured: false,
  },
  {
    id: "07",
    slug: "instrumentation",
    icon: <IconMonitor />,
    name: "Instrumentation & Monitoring",
    description:
      "Installation and real-time monitoring of geotechnical instruments including inclinometers, piezometers, and settlement markers.",
    featured: false,
  },
  {
    id: "08",
    slug: "shaft-pipe",
    icon: <IconShaft />,
    name: "Pipe Jacking",
    description:
      "Construction of shafts and manholes along with trenchless pipe jacking for underground utility installation.",
    featured: false,
  },
];

// ── Component ────────────────────────────────────────────────
const Services = () => {
  return (
    <section className="gsk-services-section">
      <div className="gsk-services-container">

        {/* Header */}
        <div className="gsk-services-header">
          <div className="gsk-services-header-left">
            <p className="gsk-services-eyebrow">What We Offer</p>
            <h2 className="gsk-services-title">
              Our <span>Areas</span> of Focus
            </h2>
            <p className="gsk-services-subtitle">
              From soil investigation to ground improvement, GSK Geotech delivers
              precision-engineered geotechnical solutions across Singapore.
            </p>
          </div>
          <div className="gsk-services-count">
            <span className="gsk-services-count-num">{servicesData.length}</span>
            <span className="gsk-services-count-label">Services</span>
          </div>
        </div>

        {/* Grid */}
        <div className="gsk-services-grid">
          {servicesData.map((service) => (
            <div
              key={service.id}
              className={`gsk-service-card${service.featured ? " featured" : ""}`}
            >
              <div className="gsk-service-icon-wrap">{service.icon}</div>
              <span className="gsk-service-num">Service {service.id}</span>
              <h3 className="gsk-service-name">{service.name}</h3>
              <p className="gsk-service-desc">{service.description}</p>
              {/* Each card now links to its own page instead of the shared #contact anchor */}
              <Link to={`/services/${service.slug}`} className="gsk-service-link">
                Learn more <IconArrow />
              </Link>
            </div>
          ))}
        </div>

        {/* CTA Strip */}
        <div className="gsk-services-cta">
          <div className="gsk-services-cta-text">
            <h3>Need a geotechnical solution?</h3>
            <p>
              Our team is ready to assist with your next project — big or small.
            </p>
          </div>
          <a href="mailto:info@gskgeotech.com" className="gsk-services-cta-btn">
            Send Mail <IconArrow />
          </a>
        </div>

      </div>
    </section>
  );
};

export default Services;